import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, OperatorFunction, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { NGXLogger } from 'ngx-logger';
import { CigpStaff } from './cigp-staff.model';
import { sortBy } from 'lodash-es';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { LocalStorageService } from 'ngx-localstorage';
import { isNotInCacheOrIsStale, showErrorDialog } from '../shared/utils';
import { STORE_STAFFS } from '../shared/constants';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { UserMappingsCount, UserMappingsList } from '../shared/mappings-count.model';

const KEY_STAFFS_SET = 'staffs_set';

function sortStaffs$(): OperatorFunction<CigpStaff[], CigpStaff[]> {
  return (source: Observable<CigpStaff[]>) => source.pipe(map((staffs: CigpStaff[]) => sortBy(staffs, 'displayName')));
}

function remapCigpStaff(staff: CigpStaff): CigpStaff {
  return {
    ...staff,
    id: `cigp-${staff.id}`,
    displayName: `${staff.displayName} (CIGP)`,
    email: staff.email.toLowerCase()
  };
}

function remapCigpStaffs$(): OperatorFunction<CigpStaff[], CigpStaff[]> {
  return (source: Observable<CigpStaff[]>) =>
    source.pipe(map((staffs: CigpStaff[]) => staffs.map((s) => remapCigpStaff(s))));
}

@Injectable({
  providedIn: 'root'
})
export class CigpStaffService {
  private apiEndpoint = `${environment.apiEndpoint}/api/cigp-staff`;
  public staffsSet$ = new BehaviorSubject<boolean>(false);
  private canEditEntitledStaffsList: boolean | undefined = undefined;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private errorDialog: MatDialog,
    private dbService: NgxIndexedDBService,
    private storage: LocalStorageService
  ) {}

  public loadCigpStaffsFromCacheAndRefreshIfNeeded$(): Observable<CigpStaff[]> {
    const staffsSet = this.storage.get<string>(KEY_STAFFS_SET);

    const obs$ = isNotInCacheOrIsStale(staffsSet)
      ? this.loadStaffsIntoCache$()
      : this.getStaffsFromCache$().pipe(switchMap((staffs) => (staffs ? of(staffs) : this.loadStaffsIntoCache$())));

    return obs$.pipe(tap(() => this.staffsSet$.next(true)));
  }

  public getStaffsFromCache$(): Observable<CigpStaff[]> {
    return this.dbService.getAll<CigpStaff>(STORE_STAFFS).pipe(
      sortStaffs$(),
      catchError((error: any) => {
        this.logger.error(error);
        showErrorDialog(this.errorDialog, error);
        return throwError(() => error);
      })
    );
  }

  public getEntitledStaffs$(): Observable<CigpStaff[]> {
    return this.http.get<string[]>(`${this.apiEndpoint}/entitled`).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        return throwError(() => error);
      }),
      map((entitledStaffsEmails) => entitledStaffsEmails.map((email) => email.toLowerCase())),
      tap((entitledStaffsEmails) =>
        this.logger.log(`Successfully loaded ${entitledStaffsEmails.length} entitled staff emails from API`)
      ),
      switchMap((entitledStaffsEmails) =>
        this.getStaffsFromCache$().pipe(
          map((cachedStaffs) => cachedStaffs.filter((s) => entitledStaffsEmails.includes(s.email)))
        )
      )
    );
  }

  public countMappingsByStaff$(): Observable<UserMappingsCount[]> {
    return this.http.get<UserMappingsCount[]>(`${this.apiEndpoint}/entitled/count-mappings`).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  public listMappingsByStaff$(): Observable<UserMappingsList[]> {
    return this.http.get<UserMappingsList[]>(`${this.apiEndpoint}/entitled/list-mappings`).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  public getStaffFromCache$(userId: string): Observable<CigpStaff> {
    return this.dbService.getByID<CigpStaff>(STORE_STAFFS, userId).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        showErrorDialog(this.errorDialog, error);
        return throwError(() => error);
      })
    );
  }

  public canEditEntitledStaffsList$(): Observable<boolean> {
    if (this.canEditEntitledStaffsList === undefined) {
      return this.http.get<boolean>(`${this.apiEndpoint}/can-i-edit-entitled-staffs`).pipe(
        catchError((error: any) => {
          this.logger.error(error);
          return throwError(() => error);
        }),
        tap((canEditEntitledStaffsList) =>
          this.logger.log(`Can edit entitled staffs list: ${canEditEntitledStaffsList}`)
        ),
        tap((canEditEntitledStaffsList) => (this.canEditEntitledStaffsList = canEditEntitledStaffsList))
      );
    } else {
      return of(this.canEditEntitledStaffsList);
    }
  }

  public addStaffsToEntitledList$(staffs: CigpStaff[]): Observable<CigpStaff[]> {
    return this.http.post<string[]>(`${this.apiEndpoint}/entitled`, staffs).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        return throwError(() => error);
      }),
      map((entitledStaffsEmails) => entitledStaffsEmails.map((email) => email.toLowerCase())),
      tap((entitledStaffsEmails) =>
        this.logger.log(`Successfully loaded ${entitledStaffsEmails.length} entitled staff emails from API`)
      ),
      switchMap((entitledStaffsEmails) =>
        this.getStaffsFromCache$().pipe(
          map((cachedStaffs) => cachedStaffs.filter((s) => entitledStaffsEmails.includes(s.email)))
        )
      )
    );
  }

  public removeStaffFromEntitledList$(userId: string): Observable<CigpStaff[]> {
    return this.http.delete<string[]>(`${this.apiEndpoint}/entitled/${userId}`).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        return throwError(() => error);
      }),
      map((entitledStaffsEmails) => entitledStaffsEmails.map((email) => email.toLowerCase())),
      tap((entitledStaffsEmails) =>
        this.logger.log(`Successfully loaded ${entitledStaffsEmails.length} entitled staff emails from API`)
      ),
      switchMap((entitledStaffsEmails) =>
        this.getStaffsFromCache$().pipe(
          map((cachedStaffs) => cachedStaffs.filter((s) => entitledStaffsEmails.includes(s.email)))
        )
      )
    );
  }

  private loadStaffsIntoCache$(): Observable<CigpStaff[]> {
    return this.http.get<CigpStaff[]>(this.apiEndpoint).pipe(
      catchError((error: any) => {
        this.logger.error(error);
        return throwError(() => error);
      }),
      tap((staffs) => this.logger.log(`Successfully loaded ${staffs.length} staffs from API`)),
      remapCigpStaffs$(),
      switchMap((staffs) => {
        return this.dbService.clear(STORE_STAFFS).pipe(
          tap(() => this.logger.log('Successfully cleared staffs DB')),
          switchMap(() =>
            this.dbService.bulkAdd<CigpStaff>(STORE_STAFFS, staffs).pipe(
              catchError((error: any) => throwError(() => error)),
              tap(() => {
                this.logger.log(`Successfully loaded ${staffs.length} staffs to DB`);
                const staffsSet = moment().toISOString();
                this.storage.set(KEY_STAFFS_SET, staffsSet);
              }),
              map(() => staffs)
            )
          )
        );
      })
    );
  }
}
